import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import FileDropZone from "@/components/FileDropZone";
import { ImageUploadField } from "@/components/ImageUploadField";
import { Field } from "@/components/PersonDialog";

export const COMPANY_TYPES = ["client","production company","distributor","sponsor","investor","supplier","agency","broadcaster","streamer","government body","council","venue","festival","training organisation","general company"];

const NEW_CONTACT = "__new__";

export function CompanyDialog({ company, people = [], onSave }) {
  const [f, setF] = useState({});
  const [contactMode, setContactMode] = useState(NEW_CONTACT);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", primary_role: "" });
  const isEdit = !!company?.id;

  useEffect(() => {
    setF({
      name: company?.name || "",
      type: company?.type || "client",
      website: company?.website || "",
      phone: company?.phone || "",
      email: company?.email || "",
      abn: company?.abn || "",
      invoice_prefix: company?.invoice_prefix || "",
      next_invoice_number: company?.next_invoice_number ?? 1,
      address: company?.address || "",
      logo_url: company?.logo_url || "",
      notes: company?.notes || "",
      tags: company?.tags || [],
      social_links: company?.social_links || {},
    });
    setContactMode(people.length ? people[0].id : NEW_CONTACT);
    setContact({ name: "", email: "", phone: "", primary_role: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const contactReady = isEdit
    || (contactMode !== NEW_CONTACT ? true : !!contact.name.trim());

  const submit = () => {
    if (isEdit) { onSave(f); return; }
    onSave(f, contactMode === NEW_CONTACT
      ? { contact: { ...contact, role_tags: ["client"] } }
      : { contact_person_id: contactMode });
  };

  return (
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>{isEdit ? "Edit company" : "Add company"}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <Field label="Name"><Input data-testid="company-name" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })}/></Field>
        <Field label="Type">
          <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
            <SelectTrigger data-testid="company-type"><SelectValue/></SelectTrigger>
            <SelectContent>{COMPANY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>

        {!isEdit && (
          <div className="rounded-lg border border-border p-3 space-y-3" data-testid="company-contact-block">
            <div>
              <Label className="text-xs">Contact person (required)</Label>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Every company needs someone to talk to. Pick one of your contacts or add a new one.
              </p>
            </div>
            <Select value={contactMode} onValueChange={setContactMode}>
              <SelectTrigger data-testid="company-contact-select"><SelectValue placeholder="Choose a contact"/></SelectTrigger>
              <SelectContent>
                <SelectItem value={NEW_CONTACT}>+ New contact person</SelectItem>
                {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {contactMode === NEW_CONTACT && (
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="company-contact-name" placeholder="Full name" value={contact.name}
                       onChange={(e) => setContact({ ...contact, name: e.target.value })}/>
                <Input data-testid="company-contact-role" placeholder="Their role" value={contact.primary_role}
                       onChange={(e) => setContact({ ...contact, primary_role: e.target.value })}/>
                <Input data-testid="company-contact-email" placeholder="Email" value={contact.email}
                       onChange={(e) => setContact({ ...contact, email: e.target.value })}/>
                <Input data-testid="company-contact-phone" placeholder="Phone" value={contact.phone}
                       onChange={(e) => setContact({ ...contact, phone: e.target.value })}/>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Website"><Input value={f.website || ""} onChange={(e) => setF({ ...f, website: e.target.value })}/></Field>
          <Field label="Email"><Input value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })}/></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone"><Input value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })}/></Field>
          <Field label="ABN / Reg No."><Input value={f.abn || ""} onChange={(e) => setF({ ...f, abn: e.target.value })}/></Field>
          <Field label="Invoice prefix (e.g. FAPA)">
            <Input value={f.invoice_prefix || ""} placeholder="Leave blank to use the workspace default"
                   data-testid="company-invoice-prefix"
                   onChange={(e) => setF({ ...f, invoice_prefix: e.target.value.toUpperCase() })}/>
          </Field>
          <Field label="Next invoice number">
            <Input type="number" min={1} value={f.next_invoice_number ?? 1} data-testid="company-next-invoice-number"
                   onChange={(e) => setF({ ...f, next_invoice_number: parseInt(e.target.value) || 1 })}/>
          </Field>
        </div>
        <Field label="Address"><Input value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })}/></Field>
        <ImageUploadField label="Company logo" testId="company-logo"
                          value={f.logo_url} onChange={(v) => setF({ ...f, logo_url: v })}
                          context={company?.id ? { company_id: company.id } : {}} />
        <Field label="Tags (comma-sep)">
          <Input data-testid="company-tags" value={(f.tags || []).join(", ")}
                 onChange={(e) => setF({ ...f, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}/>
        </Field>
        <Field label="Notes"><Textarea rows={3} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })}/></Field>
        {isEdit && (
          <div className="pt-3 border-t border-border space-y-1.5">
            <Label className="text-xs">Attachments</Label>
            <FileDropZone context={{ company_id: company.id }} compact data-testid="company-dropzone"
                          label="Drop files (logos, brand guidelines, contracts)" />
          </div>
        )}
      </div>
      <DialogFooter>
        <Button data-testid="company-save" disabled={!f.name?.trim() || !contactReady} onClick={submit}>Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}
